import { useState, useEffect, useRef } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Check, X } from "lucide-react";

/**
 * Typeahead autocomplete for Italian comuni.
 * value = { code, name, prov } | null | string (code only)
 * onChange(newVal) receives { code, name, prov } or null
 */
export default function ComuneAutocomplete({ value, onChange, placeholder = "Cerca comune…", testid = "comune-autocomplete" }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null); // {code, name, prov}
  const ref = useRef(null);

  // Initialize selected from value on mount
  useEffect(() => {
    if (!value) { setSelected(null); return; }
    if (typeof value === "object" && value.code) { setSelected(value); return; }
    // value is a code string — look it up
    api.get(`/geo/comuni?q=${encodeURIComponent(value)}`).then((r) => {
      const found = (r.data || []).find((c) => c.code === value);
      if (found) setSelected(found);
    }).catch(() => {});
  }, [value]);

  useEffect(() => {
    if (q.length < 2) { setResults([]); return; }
    const t = setTimeout(() => {
      api.get(`/geo/comuni?q=${encodeURIComponent(q)}&limit=10`)
        .then((r) => setResults(r.data || []))
        .catch(() => setResults([]));
    }, 200);
    return () => clearTimeout(t);
  }, [q]);

  // Close on outside click
  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const pick = (c) => {
    setSelected(c); onChange(c);
    setQ(""); setOpen(false);
  };
  const clear = () => { setSelected(null); onChange(null); setQ(""); };

  return (
    <div className="relative" ref={ref} data-testid={testid}>
      {selected ? (
        <div className="flex items-center gap-1 border border-primary/40 bg-primary/5 rounded-md px-3 py-1.5 cursor-pointer" onClick={clear}>
          <span className="text-sm font-medium">{selected.name}</span>
          <span className="text-[10px] text-muted-foreground">({selected.prov})</span>
          <span className="text-[10px] font-mono text-muted-foreground ml-auto">{selected.code}</span>
          <X className="w-3.5 h-3.5 text-muted-foreground ml-1"/>
        </div>
      ) : (
        <Input
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder={placeholder}
          data-testid={`${testid}-input`}
          autoComplete="off"
        />
      )}
      {open && !selected && results.length > 0 && (
        <div className="absolute z-50 mt-1 w-full max-h-60 overflow-y-auto bg-card border border-border rounded-md shadow-lg" data-testid={`${testid}-results`}>
          {results.map((c) => (
            <button key={`${c.code}-${c.prov}`} type="button"
              onMouseDown={(e) => { e.preventDefault(); pick(c); }}
              className="w-full text-left px-3 py-2 hover:bg-primary/5 border-b border-border last:border-b-0 flex items-center gap-2"
              data-testid={`${testid}-opt-${c.code}`}
            >
              <span className="text-sm">{c.name}</span>
              <span className="text-[10px] text-muted-foreground">({c.prov})</span>
              <span className="text-[10px] font-mono text-muted-foreground ml-auto">{c.code}</span>
              <Check className="w-3 h-3 text-primary opacity-0 group-hover:opacity-100"/>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
